import {Component, computed, effect, inject, model, ModelSignal, Signal} from '@angular/core';
import {
  DialogService,
  ICustomGridRow,
  ITableAction,
  ReasoningDialogComponent,
  SharedGridOptions,
  TableControlComponent,
  TableControlModule,
} from 'frontier/browserkit';
import {changes, EControlActions, EsvgFiles, IControlDataChanged, INITIAL_STATE_TOKEN} from 'frontier/nucleus';
import {ECalculusStateFilter} from '../../../../../config/state-filter.enum';
import {concatMap, filter, finalize, tap} from 'rxjs/operators';
import {Observable, of} from 'rxjs';
import {ThemePalette} from '@angular/material/core';
import {ProcessOverviewStore} from '../../../service/process-overview.store';
import AppControlGuids from '../../../../../../api/ControlGuids';
import {ICart} from '../../../service/state.interface';
import {AppService, InvoiceListService} from '../../../../../../api/generated';
import {GridOptions} from 'ag-grid-community';

@Component({
  selector: 'app-invoice-list',
  templateUrl: '../../../../../../../../projects/frontier/browserkit/src/lib/components/control/table-control/table-control.component.html',
  styleUrl: '../../../../../../../../projects/frontier/browserkit/src/lib/components/control/table-control/table-control.component.scss',
  standalone: true,
  imports: [TableControlModule],
  providers: [
    {provide: INITIAL_STATE_TOKEN, useValue: {filter: null}},
    {provide: TableControlComponent, useExisting: InvoiceListComponent},
  ],
})
export class InvoiceListComponent extends TableControlComponent {
  private readonly _dialogService: DialogService = inject(DialogService);
  private readonly _processOverviewService: ProcessOverviewStore = inject(ProcessOverviewStore);
  private _appService: AppService = inject(AppService);
  private _invoiceListService: InvoiceListService = inject(InvoiceListService);
  readonly selectedCart: Signal<ICart> = this._processOverviewService.selectedCart;
  private readonly _selectedState: Signal<ECalculusStateFilter>
    = computed(() => this._processOverviewService.selectedProcess().state);

  override GUID: string = AppControlGuids.TInvoiceList;
  override actions: ModelSignal<ITableAction[]> = model<ITableAction[]>([
    {
      displayName: 'Rechnung stornieren',
      controlAction: EControlActions.cancel,
      disabledIfNoSelection: true,
      disabledIfMultiSelection: true,
      color: 'warn' as ThemePalette,
      icon: EsvgFiles.close,
      action: () => this.cancelAndCreate(),
      isHidden: (): boolean => {
        return this._selectedState() === ECalculusStateFilter.monitorPaymentOk;
      },
    },
    {
      displayName: 'Gutschrift',
      controlAction: EControlActions.credit,
      disabledIfNoSelection: true,
      disabledIfMultiSelection: true,
      color: 'warn' as ThemePalette,
      icon: EsvgFiles.close,
      action: () => this.cancelAndNewAfterPayed(),
      isHidden: () => {
        return !(this._selectedState() === ECalculusStateFilter.monitorPaymentOk);
      },
    },
    {
      displayName: 'Uneinbringlich rückgängig',
      controlAction: EControlActions.rollback,
      disabledIfNoSelection: true,
      disabledIfMultiSelection: true,
      color: 'warn' as ThemePalette,
      icon: EsvgFiles.reset,
      action: (): Observable<boolean> => this.rollbackUncollectible(),
      isHidden: (): boolean => {
        return !(this._selectedState() === ECalculusStateFilter.monitorPaymentOk);
      },
    },
    {
      displayName: 'Uneinbringlich',
      controlAction: EControlActions.uncollectible,
      disabledIfNoSelection: true,
      disabledIfMultiSelection: true,
      color: 'warn' as ThemePalette,
      icon: EsvgFiles.block,
      action: () => this.setUncollectible(),
      isHidden: (): boolean => {
        return this._selectedState() === ECalculusStateFilter.monitorPaymentOk;
      },
    },
    {
      displayName: 'Inkassoübergabe',
      controlAction: EControlActions.passToCollectionAgency,
      disabledIfNoSelection: true,
      disabledIfMultiSelection: true,
      color: 'warn' as ThemePalette,
      icon: EsvgFiles.block,
      action: () => this.passToCollectionAgency(),
      isHidden: (): boolean => {
        return this._selectedState() === ECalculusStateFilter.monitorPaymentOk;
      },
    },
    {
      displayName: 'Bezahlt',
      controlAction: EControlActions.payed,
      disabledIfNoSelection: true,
      disabledIfMultiSelection: true,
      color: 'accent' as ThemePalette,
      icon: EsvgFiles.money,
      action: () => this.setPayed(),
      isHidden: (): boolean => {
        return this._selectedState() === ECalculusStateFilter.monitorPaymentOk;
      },
    },
    {
      displayName: 'Storno rückgängig',
      controlAction: EControlActions.reroll,
      disabledIfNoSelection: true,
      disabledIfMultiSelection: true,
      color: 'accent' as ThemePalette,
      icon: EsvgFiles.reset,
      action: () => this.rollbackCancelAndNew(),
      isHidden: (): boolean => {
        return this._selectedState() === ECalculusStateFilter.monitorPaymentOk;
      },
    },
  ]);
  override gridOptions: GridOptions = SharedGridOptions;

  constructor() {
    super();

    effect((): void => {
      const selectedCart: ICart = this.selectedCart();
      if (selectedCart) {
        this.patchFilter({
          parent: selectedCart.obj,
          typename: 'cart',
        });
      }
    }, {allowSignalWrites: true});

    this.subs.add(
      this.controlStore.controlDataChanged$.pipe(
        filter((evt: IControlDataChanged): boolean => {
          return new Map([
            [AppControlGuids.TAllClientList, new Set([EControlActions.attendance])],
            [AppControlGuids.TCartList, new Set([EControlActions.invoice, EControlActions.changeElsid])],
            [AppControlGuids.TCartItemsList,
              new Set([
                ...changes,
                EControlActions.partlyCancel,
                EControlActions.cancelItemValue,
                EControlActions.invoice,
              ])],
            [AppControlGuids.TInvoiceList,
              new Set([
                ...changes,
                EControlActions.cancel,
                EControlActions.uncollectible,
                EControlActions.passToCollectionAgency,
                EControlActions.payed,
                EControlActions.credit,
                EControlActions.reroll,
                EControlActions.partlyCancel,
              ])],
          ]).get(evt.GUID)?.has(evt.changeType);
        }),
        tap((evt: IControlDataChanged): void => {
          if (evt.GUID === AppControlGuids.TInvoiceList
            || evt.GUID === AppControlGuids.TCartItemsList
          ) {
            this.changeAndFetchInstance();
          } else {
            this.refreshInfiniteCache();
          }
        })).subscribe()
    );
  }

  private cancelAndCreate(): Observable<boolean> {
    return this._invoiceListService.invoiceListcanceleAndNew(
      {
        invoices: this.selectedRows().map((row) => {
          return row.apiRow.obj;
        })
      }
    ).pipe(finalize(() => this.gridApi?.hideOverlay()));
  }

  private setUncollectible(): Observable<boolean | null> {
    return this._dialogService.dialog.open(ReasoningDialogComponent, {
      data: {
        title: 'Begründung'
      }
    }).afterClosed().pipe(
      concatMap((reason: string): Observable<boolean> => {
          if (reason) {
            return this._invoiceListService.invoiceListUncollectible({
              invoices: this.selectedRows().map((row: ICustomGridRow) => {
                  return row.apiRow.obj;
                }
              ),
              reason,
            })
          }
          return of(null);
        }
      ),
      finalize(
        () => this.gridApi?.hideOverlay()
      )
    )
  }

  private passToCollectionAgency(): Observable<boolean | null> {
    return this._dialogService.dialog.open(ReasoningDialogComponent, {
      data: {
        title: 'Zusatzinformation'
      }
    }).afterClosed().pipe(
      concatMap((reason: string): Observable<boolean> => {
          if (reason) {
            return this._invoiceListService.invoiceListPassToCollectionAgency({
              invoices: this.selectedRows().map((row: ICustomGridRow) => {
                  return row.apiRow.obj;
                }
              ),
              reason,
            })
          }
          return of(null);
        }
      ),
      finalize(
        () => this.gridApi?.hideOverlay()
      )
    )
  }

  private setPayed(): Observable<boolean> {
    return this._invoiceListService.invoiceListpayed(
      {
        invoices: this.selectedRows().map((row: ICustomGridRow) => {
          return row.apiRow.obj;
        }),
      }
    ).pipe(finalize(() => this.gridApi?.hideOverlay()));
  }


  private cancelAndNewAfterPayed(): Observable<boolean> {
    return this._invoiceListService.invoiceListcanceleAndNewAfterPayed(
      {
        invoices: this.selectedRows().map((row: ICustomGridRow) => {
          return row.apiRow.obj;
        }),
      }
    ).pipe(finalize(() => this.gridApi?.hideOverlay()));
  }

  private rollbackCancelAndNew(): Observable<boolean> {
    return this._invoiceListService.invoiceListrollbackCanceleAndNew(
      {invoice: this.selectedRows()[0].apiRow.obj}
    )
      .pipe(finalize(() => this.gridApi?.hideOverlay()));
  }

  private rollbackUncollectible(): Observable<boolean> {
    return this._appService.invoiceListrollbackUncollectible(
      {invoice: this.selectedRows()[0].apiRow.obj}
    )
      .pipe(finalize((): void => this.gridApi?.hideOverlay()));
  }
}
